import React, { useState, useEffect, Suspense } from "react";
import { Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import SEO from "./components/SEO";
import DataPreloader from "./components/DataPreloader";
import DynamicSEO from "./components/DynamicSEO";
import AnalyticsDebugger from "./components/AnalyticsDebugger";
import SimpleAnalytics from "./components/SimpleAnalytics";
import { ToastContainer, useToast } from "./components/ToastNotification";
// Lazy load SEO components to reduce initial bundle size
const SEOPerformanceMonitor = React.lazy(
  () => import("./components/SEOPerformanceMonitor"),
);
const SEOAudit = React.lazy(() => import("./components/SEOAudit"));
import { generateStructuredData } from "./utils/seo";
import { usePageTracking } from "./hooks/useSEO";

// Lazy load page components for code splitting
const Home = React.lazy(() => import("./pages/Home"));
const Courses = React.lazy(() => import("./pages/Courses"));
const CourseDetail = React.lazy(() => import("./pages/CourseDetail"));
const Syllabus = React.lazy(() => import("./pages/Syllabus"));
const MicroSyllabusDetail = React.lazy(
  () => import("./pages/MicroSyllabusDetail"),
);
const CourseList = React.lazy(() => import("./pages/course/CourseList"));
const ReadingMaterial = React.lazy(() => import("./pages/ReadingMaterial"));
const ReadingMaterialDetail = React.lazy(
  () => import("./pages/ReadingMaterialDetail"),
);
const SelfAssessment = React.lazy(() => import("./pages/SelfAssessment"));

const About = React.lazy(() => import("./pages/About"));
const Contact = React.lazy(() => import("./pages/Contact"));
const PDFDownloadDemo = React.lazy(
  () => import("./components/examples/PDFDownloadDemo"),
);
const NotFound = React.lazy(() => import("./pages/NotFound"));
const PrivacyPolicy = React.lazy(() => import("./pages/legal/PrivacyPolicy"));
const TermsOfService = React.lazy(() => import("./pages/legal/TermsOfService"));
const CookiePolicy = React.lazy(() => import("./pages/legal/CookiePolicy"));
const DesignShowcase = React.lazy(() => import("./pages/DesignShowcase"));

// Loading component for Suspense fallback
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-[50vh]">
    <div className="animate-spin rounded-full h-12 w-12 border-2 border-gray-200 border-t-gray-900"></div>
  </div>
);

function App() {
  const [showSEOAudit, setShowSEOAudit] = useState(false);

  // Global toast notifications
  const { toasts, removeToast } = useToast();

  // Use page tracking hook for analytics
  usePageTracking();

  // Base structured data for the website
  const websiteStructuredData = [
    generateStructuredData("WebSite"),
    generateStructuredData("Organization"),
  ];

  // Enable SEO audit in development mode
  useEffect(() => {
    const enableAudit =
      import.meta.env.VITE_ENABLE_SEO_AUDIT === "true" || import.meta.env.DEV;

    // Keyboard shortcut to toggle SEO audit (Ctrl/Cmd + Shift + S)
    const handleKeyDown = (e) => {
      if (enableAudit && e.ctrlKey && e.shiftKey && e.key === "S") {
        e.preventDefault();
        setShowSEOAudit((prev) => !prev);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <HelmetProvider>
      <div className="min-h-screen flex flex-col bg-white">
        {/* Background data preloader */}
        <DataPreloader
          preloadOnMount={true}
          criticalData={["categories"]}
          secondaryData={["reading-materials", "courses"]}
          debug={import.meta.env.VITE_DEBUG_MODE === "true"}
        />
        {/* Analytics and Performance Monitoring */}
        <DynamicSEO />
        <SimpleAnalytics />

        <Suspense fallback={null}>
          <SEOPerformanceMonitor
            enableReporting={
              import.meta.env.VITE_ENABLE_PERFORMANCE_MONITORING !== "false"
            }
            debug={import.meta.env.VITE_DEBUG_MODE === "true"}
          />
        </Suspense>

        {/* Base SEO for the entire application */}
        <SEO
          structuredData={websiteStructuredData}
          title="WebDoko Reading Materials - BSc CSIT, BCA, BIM, MCA Resources"
          description="Comprehensive collection of books, syllabus, reading materials and MCQs for Nepal university programs including BSc. CSIT, BCA, BIM, MCA, MIT, MBS from TU, KU, PU."
          keywords={[
            "WebDoko resources",
            "BSc CSIT books",
            "BCA syllabus",
            "BIM materials",
            "MCA textbooks",
            "TU curriculum",
            "KU books",
            "PU syllabus",
            "free study materials",
            "university textbooks Nepal",
          ]}
        />

        <ScrollToTop />
        <Navbar />
        <main className="flex-grow">
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              <Route path="/" element={<Home />} />
              {/* <Route path="/courses" element={<Courses />} />
              <Route path="/courses/:slug" element={<CourseDetail />} />*/}
              <Route path="/syllabus" element={<Syllabus />} />
              <Route path="/syllabus/:category" element={<CourseList />} />
              <Route
                path="/syllabus/:category/:slug"
                element={<MicroSyllabusDetail />}
              />
              <Route path="/reading-material" element={<ReadingMaterial />} />
              <Route
                path="/reading-material/:category/:slug"
                element={<ReadingMaterialDetail />}
              />
              <Route path="/mcqs" element={<SelfAssessment />} />

              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/cookies" element={<CookiePolicy />} />
              <Route path="/design" element={<DesignShowcase />} />
              <Route path="/pdf-demo" element={<PDFDownloadDemo />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </main>
        <Footer />

        {/* SEO Audit Panel - Development and Testing */}
        {showSEOAudit && (
          <Suspense fallback={<div>Loading SEO Audit...</div>}>
            <SEOAudit
              showAudit={showSEOAudit}
              onToggle={() => setShowSEOAudit(!showSEOAudit)}
              autoRun={true}
              detailed={import.meta.env.VITE_DEBUG_MODE === "true"}
            />
          </Suspense>
        )}

        {/* Development SEO Indicator */}
        {(import.meta.env.VITE_ENABLE_SEO_AUDIT === "true" ||
          import.meta.env.DEV) && (
          <div
            className="fixed bottom-4 right-4 z-50 bg-gray-900 text-white px-4 py-3 text-sm cursor-pointer hover:bg-gray-800 transition-colors uppercase tracking-wide font-medium"
            onClick={() => setShowSEOAudit(!showSEOAudit)}
            title="Click to toggle SEO Audit (Ctrl+Shift+S)"
          >
            SEO
          </div>
        )}
      </div>

      {/* Analytics Debugger for development */}
      <AnalyticsDebugger />

      {/* Global Toast Notifications */}
      <ToastContainer
        toasts={toasts}
        onRemoveToast={removeToast}
        position="top-right"
      />
    </HelmetProvider>
  );
}

export default App;
